import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar, GridPaginationModel } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Ground, PaginatedResponse } from '../models';
import { groundApi } from '../services/api';

const Grounds: React.FC = () => {
  const [grounds, setGrounds] = useState<Ground[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [editingGround, setEditingGround] = useState<Partial<Ground>>({});
  const [isNew, setIsNew] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchGrounds = useCallback(async (page = paginationModel.page, pageSize = paginationModel.pageSize, search = searchTerm) => {
    setLoading(true);
    try {
      const response = await groundApi.getAll({ 
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
        search: search
      });
      
      const typedResponse = response.data as PaginatedResponse<Ground>;
      setGrounds(typedResponse.results);
      setTotalCount(typedResponse.count);
    } catch (error) {
      console.error('Error fetching grounds:', error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
    fetchGrounds(newModel.page, newModel.pageSize);
  }, [fetchGrounds]);

  const [isSearching, setIsSearching] = useState(false);
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setIsSearching(true);
    
    // Debounce search requests
    const timeoutId = setTimeout(() => {
      fetchGrounds(0, paginationModel.pageSize, newSearchTerm);
      setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page on search
      setIsSearching(false);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  useEffect(() => {
    fetchGrounds();
  }, [fetchGrounds]);

  const handleEdit = (ground: Ground) => {
    setEditingGround(ground);
    setIsNew(false);
    setOpen(true);
  };

  const handleAdd = () => {
    setEditingGround({
      name: '',
      city: '',
      country: '',
      is_international: false,
    });
    setIsNew(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setEditingGround((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        await groundApi.create(editingGround);
      } else {
        await groundApi.update(editingGround.id!, editingGround);
      }
      fetchGrounds();
      setOpen(false);
    } catch (error) {
      console.error('Error saving ground:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'image', 
      headerName: 'Image', 
      width: 80,
      sortable: false,
      renderCell: (params) => {
        return params.row.image ? (
          <img 
            src={params.row.image} 
            alt={params.row.name} 
            style={{ 
              width: '40px', 
              height: '40px', 
              objectFit: 'cover',
              borderRadius: '4px'
            }}
          />
        ) : (
          <Box sx={{ 
            width: '40px', 
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.300',
            color: 'white',
            borderRadius: '4px'
          }}>
            {params.row.name.charAt(0)}
          </Box>
        );
      }
    },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'country', headerName: 'Country', width: 150 },
    { field: 'capacity', headerName: 'Capacity', width: 120 },
    {
      field: 'is_international',
      headerName: 'International',
      width: 120,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      renderCell: (params) => (
        <Box>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <Button 
            size="small" 
            onClick={() => {
              window.location.href = `/matches?ground=${params.row.id}`;
            }}
          >
            View Matches
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Grounds
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => fetchGrounds()}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Ground
          </Button>
        </Box>
      </Box>
      
      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search grounds by name, city, or country..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">
                  Searching...
                </Typography>
              </InputAdornment>
            ) : null,
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      
      <Paper sx={{ height: 'calc(100% - 120px)', width: '100%' }}>
        <DataGrid
          rows={grounds}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalCount}
          disableColumnFilter
          density="compact"
        />
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{isNew ? 'Add New Ground' : 'Edit Ground'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="name"
              label="Name"
              fullWidth
              value={editingGround.name || ''}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="small_name"
              label="Short Name"
              fullWidth
              value={editingGround.small_name || ''}
              onChange={handleInputChange}
            />
            <TextField
              name="city"
              label="City"
              fullWidth
              value={editingGround.city || ''}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="country"
              label="Country"
              fullWidth
              value={editingGround.country || ''}
              onChange={handleInputChange}
            />
            <TextField
              name="capacity"
              label="Capacity"
              fullWidth
              type="number"
              value={editingGround.capacity || ''}
              onChange={handleInputChange}
            />
            <FormControlLabel
              control={
                <Switch
                  name="is_international"
                  checked={editingGround.is_international || false}
                  onChange={handleInputChange}
                />
              }
              label="International Venue"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Grounds;
